import type {
  CurrentStatusState,
  DayBucket,
  DependabilityV2Tier,
  LastActiveBucket,
  TierBoundaryLite,
  WorkerClearance,
  WorkerCurrentStatus,
  WorkerStatuses,
} from '@/services/workersV2API';
import {
  BACKGROUND_STATUS_MAP,
  I9_STATUS_MAP,
} from '../WorkersV2/components/StatusMappings';

export type Option<T extends string = string> = { label: string; value: T };

export const TAB_OPTIONS: Option<
  'all' | 'onboarding' | 'background_check' | 'i9'
>[] = [
  { label: 'All', value: 'all' },
  { label: 'Onboarding', value: 'onboarding' },
  { label: 'Background check', value: 'background_check' },
  { label: 'I-9', value: 'i9' },
];

export const TAB_STATE: Record<
  Exclude<(typeof TAB_OPTIONS)[number]['value'], 'all'>,
  CurrentStatusState
> = {
  onboarding: 'onboarding',
  background_check: 'bg_check_pending',
  i9: 'i9_pending',
};

export const TAX_TYPE_OPTIONS: Option<'1099' | 'w2'>[] = [
  { label: '1099', value: '1099' },
  { label: 'W2', value: 'w2' },
];

export const TIER_OPTIONS: Option<DependabilityV2Tier>[] = [
  { label: 'Monitored', value: 'monitored' },
  { label: 'Standard', value: 'standard' },
  { label: 'Gold', value: 'gold' },
  { label: 'Platinum', value: 'platinum' },
];

export const LAST_ACTIVE_OPTIONS: Option<LastActiveBucket>[] = [
  { label: 'Today', value: 'today' },
  { label: 'Within 7 days', value: 'up_to_7_days' },
  { label: '8-14 days', value: '8_to_14_days' },
  { label: '15+ days', value: '15_plus_days' },
];

export const STATUS_OPTIONS: Option<'true' | 'false'>[] = [
  { label: 'Enabled', value: 'true' },
  { label: 'Disabled', value: 'false' },
];

export type SuspensionFilter = 'active' | 'suspended' | 'permanently_removed';

// GRAVY-10836: the legacy Workers page's "Suspension Status" options. The
// stored value for the last one is `permanently_removed` (the legacy list
// sends `permanently_suspended`, which no worker row carries).
export const SUSPENSION_OPTIONS: Option<SuspensionFilter>[] = [
  { label: 'None', value: 'active' },
  { label: 'Suspended', value: 'suspended' },
  { label: 'Permanently Removed', value: 'permanently_removed' },
];

export const PROGRESS_OPTIONS: Option<DayBucket>[] = [
  { label: 'Within 7 days elapsed', value: 'up_to_7_days' },
  { label: '8-14 days elapsed', value: '8_to_14_days' },
  { label: '15+ days stalled', value: '15_plus_days' },
];

export const RADIUS = { min: 1, max: 100, default: 5 };

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfLocalDay = (d: Date): number =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

// "today" is a calendar-date match in the viewer's zone, matching graphy's
// `last_seen_at >= beginning_of_day` today bucket. Anything earlier still
// floors to whole days.
export const formatActiveAgo = (
  iso: string | null | undefined,
  now: Date = new Date(),
): string => {
  if (!iso) return '—';
  const days = Math.floor(
    (startOfLocalDay(now) - startOfLocalDay(new Date(iso))) / DAY_MS,
  );
  if (days <= 0) return 'active today';
  return `active ${days}d ago`;
};

const CLEARED_BG = new Set(BACKGROUND_STATUS_MAP.Approved);
const CLEARED_I9 = new Set(I9_STATUS_MAP.Completed);

// `statuses` is optional here although the list type has it: a row without
// it threw in every table cell that asks about clearance and blanked the
// page (BH-33).
type ClearanceSource = {
  clearance?: WorkerClearance;
  statuses?: Partial<Pick<WorkerStatuses, 'checkr_status' | 'i9_status'>>;
};

// The backend's `clearance` booleans are the rule; the status maps are only
// a fallback for a graphy that predates GRAVY-10436.
export const bgCheckCleared = (w: ClearanceSource): boolean =>
  w.clearance
    ? w.clearance.bg_check
    : CLEARED_BG.has((w.statuses?.checkr_status ?? '').toLowerCase());

export const i9Cleared = (w: ClearanceSource): boolean =>
  w.clearance
    ? w.clearance.i9
    : CLEARED_I9.has((w.statuses?.i9_status ?? '').toLowerCase());

// An E-Verify tentative non-confirmation counts as a cleared I-9 in graphy
// (Product Q5), but no screen may call it "verified": the icon label
// (GRAVY-10988) and the side panel's I-9 step marker (BH-37) both use this.
export const i9Mismatch = (w: ClearanceSource): boolean =>
  (w.statuses?.i9_status ?? '').toLowerCase() === 'tentative_nc';

export const i9IconLabel = (w: ClearanceSource, notDone: string): string => {
  if (!i9Cleared(w)) return notDone;
  return i9Mismatch(w) ? 'I-9 info mismatch' : 'I-9 verified';
};

export const formatTaxType = (raw: string | null | undefined): string => {
  if (!raw) return '—';
  return raw.toLowerCase() === 'w2' ? 'W2' : raw.toUpperCase();
};

export const statusPillLabel = (cs: WorkerCurrentStatus): string => {
  switch (cs.state) {
    case 'onboarding':
      return `Onboarding (${cs.percentage ?? 0}%)`;
    case 'bg_check_pending':
      return 'BG check pending';
    case 'i9_pending':
      return 'I-9 pending';
    case 'disabled':
      return 'Disabled';
    default:
      return 'Enabled';
  }
};

export const statusSubtext = (cs: WorkerCurrentStatus): string | null => {
  if (cs.elapsed_days === null || cs.elapsed_days === undefined) return null;
  return `${cs.elapsed_days}d ${cs.stalled ? 'stalled' : 'elapsed'}`;
};

export const tierRangeLabel = (
  b: TierBoundaryLite,
  index: number,
  count: number,
): string => {
  if (index === 0) return `<${b.max + 1}`;
  if (index === count - 1) return `${b.min}+`;
  return `${b.min}-${b.max}`;
};

export const formatPhone = (raw: string | null | undefined): string => {
  const digits = (raw ?? '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  if (digits.length !== 10) return raw ?? '';
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};
